import { useState, useEffect, useCallback } from 'react';
import { api } from '@/App';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { toast } from 'sonner';
import { FileCheck, Plus, Check, Package, Truck } from 'lucide-react';
import { EnterpriseToolbar, EnterpriseTableHead, StatusBadge, EmptyState, SelectCheckbox, RowActions } from '@/components/EnterpriseTable';

export default function GRNPage() {
  const [grns, setGRNs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [grnDialog, setGRNDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(false);
  const [selectedGRN, setSelectedGRN] = useState(null);
  const [selectedPO, setSelectedPO] = useState(null);
  const [grnForm, setGRNForm] = useState({ po_id: '', store_id: '', invoice_number: '', invoice_date: '', notes: '', items: [] });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [grnsRes, posRes, storesRes] = await Promise.all([
        api.get('/grn'),
        api.get('/purchase-orders'),
        api.get('/stores')
      ]);
      setGRNs(grnsRes.data);
      setOrders(posRes.data.filter(po => po.status === 'sent' || po.status === 'partially_received'));
      setStores(storesRes.data);
    } catch (err) {
      toast.error('Failed to load GRN data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openGRNDialog = (po) => {
    setSelectedPO(po);
    setGRNForm({
      po_id: po.id,
      store_id: stores[0]?.id || '',
      invoice_number: '',
      invoice_date: new Date().toISOString().split('T')[0],
      notes: '',
      items: po.items.map((item, idx) => ({
        po_item_index: idx,
        item_name: item.item_name,
        ordered_quantity: item.quantity,
        received_quantity: item.quantity - (item.received_quantity || 0),
        unit: item.unit,
        unit_price: item.unit_price
      }))
    });
    setGRNDialog(true);
  };

  const handleCreateGRN = async () => {
    try {
      const res = await api.post('/grn', grnForm);
      await api.put(`/grn/${res.data.id}/confirm`);
      toast.success('GRN created and stock updated');
      setGRNDialog(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create GRN');
    }
  };

  const handleConfirmGRN = async (grnId) => {
    try {
      await api.put(`/grn/${grnId}/confirm`);
      toast.success('GRN confirmed and stock updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to confirm GRN');
    }
  };

  const updateGRNItem = (idx, field, value) => {
    const newItems = [...grnForm.items];
    newItems[idx][field] = value;
    setGRNForm({ ...grnForm, items: newItems });
  };

  const getStoreName = (storeId) => stores.find(s => s.id === storeId)?.name || storeId;

  const filteredGRNs = grns.filter(g => 
    g.grn_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    g.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredGRNs.length) setSelectedIds([]);
    else setSelectedIds(filteredGRNs.map(g => g.id));
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="h-12 w-12 rounded-full border-4 border-emerald-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-slate-50" data-testid="grn-page">
      <EnterpriseToolbar
        title="Goods Receipt Notes"
        totalCount={filteredGRNs.length}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onRefresh={fetchData}
        onView={() => {
          const grn = grns.find(g => g.id === selectedIds[0]);
          if (grn) { setSelectedGRN(grn); setViewDialog(true); }
        }}
        onPrint={() => window.print()}
        selectedCount={selectedIds.length}
        showAdvancedSearch={false}
      />

      {/* Pending POs for GRN */}
      {orders.length > 0 && (
        <div className="px-2 pt-1">
          <Card className="border-amber-200 bg-amber-50/50">
            <CardContent className="py-2 px-2">
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5">
                  <Truck className="h-4 w-4 text-amber-600" />
                  <h3 className="font-semibold text-xs text-amber-800">Pending POs ({orders.length})</h3>
                </div>
                <Badge className="bg-amber-100 text-amber-700 text-[9px] px-1.5">Awaiting Receipt</Badge>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-1.5">
                {orders.slice(0, 6).map(po => (
                  <div key={po.id} className="bg-white p-1.5 rounded border border-amber-100 hover:border-amber-300 transition-colors">
                    <div className="flex justify-between items-start mb-1">
                      <div>
                        <p className="font-mono text-[10px] font-semibold text-slate-800">{po.po_number}</p>
                        <p className="text-[9px] text-slate-500 truncate">{po.vendor_name}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-[9px] text-slate-500">{po.items?.length} items</p>
                      <Button size="sm" onClick={() => openGRNDialog(po)} className="h-5 text-[9px] px-1.5 bg-emerald-600 hover:bg-emerald-700">
                        <Plus className="h-2.5 w-2.5 mr-0.5" />Receive
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* GRN List */}
      <div className="flex-1 overflow-auto">
        <Card className="mt-1 border-slate-200 shadow-sm rounded-none border-x-0">
          <CardContent className="p-0">
            <Table>
              <thead>
                <tr className="bg-slate-100 border-b">
                  <EnterpriseTableHead className="w-12">
                    <SelectCheckbox 
                      checked={selectedIds.length === filteredGRNs.length && filteredGRNs.length > 0}
                      indeterminate={selectedIds.length > 0 && selectedIds.length < filteredGRNs.length}
                      onCheckedChange={toggleSelectAll}
                    />
                  </EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable className="w-16">S.No</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>GRN Number</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>GRN Date</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>PO Number</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Vendor Invoice</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Store</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Items</EnterpriseTableHead>
                  <EnterpriseTableHead sortable filterable>Status</EnterpriseTableHead>
                  <EnterpriseTableHead className="w-24">Actions</EnterpriseTableHead>
                </tr>
              </thead>
              <tbody>
                {filteredGRNs.map((grn, index) => (
                  <TableRow key={grn.id} className={`hover:bg-slate-50 ${selectedIds.includes(grn.id) ? 'bg-blue-50' : ''}`}>
                    <TableCell className="py-1 px-1.5">
                      <SelectCheckbox checked={selectedIds.includes(grn.id)} onCheckedChange={() => toggleSelect(grn.id)} />
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-500 py-1 px-1.5">{index + 1}</TableCell>
                    <TableCell className="font-mono text-xs font-medium text-slate-800 py-1 px-1.5">{grn.grn_number}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">
                      {new Date(grn.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-slate-600 py-1 px-1.5">{grn.po_number || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">{grn.invoice_number || '-'}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">{getStoreName(grn.store_id)}</TableCell>
                    <TableCell className="text-xs text-slate-600 py-1 px-1.5">{grn.items?.length || 0}</TableCell>
                    <TableCell className="py-1 px-1.5"><StatusBadge status={grn.status} /></TableCell>
                    <TableCell className="py-1 px-1.5">
                      <RowActions
                        onView={() => { setSelectedGRN(grn); setViewDialog(true); }}
                        customActions={grn.status === 'draft' ? [{
                          icon: Check, onClick: () => handleConfirmGRN(grn.id), title: 'Confirm', className: 'text-emerald-500'
                        }] : []}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
            
            {filteredGRNs.length === 0 && (
              <EmptyState
                icon={FileCheck}
                title="No GRNs Found"
                description="Create a GRN when you receive goods against a purchase order"
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create GRN Dialog */}
      <Dialog open={grnDialog} onOpenChange={setGRNDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold">Receive Goods - {selectedPO?.po_number}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            <div className="grid grid-cols-3 gap-4 p-4 bg-slate-50 rounded-lg">
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Receive into Store *</Label>
                <Select value={grnForm.store_id} onValueChange={(v) => setGRNForm({...grnForm, store_id: v})}>
                  <SelectTrigger className="bg-white"><SelectValue placeholder="Select store" /></SelectTrigger>
                  <SelectContent>
                    {stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Vendor Invoice #</Label>
                <Input value={grnForm.invoice_number} onChange={(e) => setGRNForm({...grnForm, invoice_number: e.target.value})} placeholder="INV-001" className="bg-white" />
              </div>
              <div className="grid gap-2">
                <Label className="text-xs font-medium text-slate-500 uppercase">Invoice Date</Label>
                <Input type="date" value={grnForm.invoice_date} onChange={(e) => setGRNForm({...grnForm, invoice_date: e.target.value})} className="bg-white" />
              </div>
            </div>
            
            <div className="space-y-3">
              <Label className="text-sm font-semibold">Items to Receive</Label>
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <thead>
                    <tr className="bg-slate-100">
                      <EnterpriseTableHead className="w-12">#</EnterpriseTableHead>
                      <EnterpriseTableHead>Item Description</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-28 text-center">Ordered</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-32 text-center">Receiving</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-24">Unit</EnterpriseTableHead>
                      <EnterpriseTableHead className="w-32 text-right">Unit Price</EnterpriseTableHead>
                    </tr>
                  </thead>
                  <tbody>
                    {grnForm.items.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-center text-slate-500">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-center">{item.ordered_quantity}</TableCell>
                        <TableCell>
                          <Input 
                            type="number" 
                            className="w-24 text-center mx-auto"
                            value={item.received_quantity} 
                            onChange={(e) => updateGRNItem(idx, 'received_quantity', Number(e.target.value))}
                            max={item.ordered_quantity}
                          />
                        </TableCell>
                        <TableCell>{item.unit}</TableCell>
                        <TableCell className="text-right font-mono">₹{item.unit_price}</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="grid gap-2">
              <Label className="text-xs font-medium text-slate-500 uppercase">Remarks</Label>
              <Textarea value={grnForm.notes} onChange={(e) => setGRNForm({...grnForm, notes: e.target.value})} placeholder="Any remarks about the delivery..." className="min-h-[60px]" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setGRNDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateGRN} className="bg-emerald-600 hover:bg-emerald-700">
              <Package className="h-4 w-4 mr-2" />Receive & Update Stock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View GRN Dialog */}
      <Dialog open={viewDialog} onOpenChange={setViewDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-semibold">GRN: {selectedGRN?.grn_number}</DialogTitle>
              <StatusBadge status={selectedGRN?.status} />
            </div>
          </DialogHeader>
          {selectedGRN && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg text-sm">
                <div><span className="text-slate-500">PO Number:</span><p className="font-medium font-mono">{selectedGRN.po_number || '-'}</p></div>
                <div><span className="text-slate-500">Store:</span><p className="font-medium">{getStoreName(selectedGRN.store_id)}</p></div>
                <div><span className="text-slate-500">Invoice #:</span><p className="font-medium">{selectedGRN.invoice_number || '-'}</p></div>
                <div><span className="text-slate-500">Date:</span><p className="font-medium">{new Date(selectedGRN.created_at).toLocaleDateString('en-IN')}</p></div>
              </div>
              
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <thead><tr className="bg-slate-100">
                    <EnterpriseTableHead className="w-12">#</EnterpriseTableHead>
                    <EnterpriseTableHead>Item</EnterpriseTableHead>
                    <EnterpriseTableHead className="text-center">Qty</EnterpriseTableHead>
                    <EnterpriseTableHead className="text-right">Unit Price</EnterpriseTableHead>
                    <EnterpriseTableHead className="text-right">Amount</EnterpriseTableHead>
                  </tr></thead>
                  <tbody>
                    {selectedGRN.items?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="text-slate-500">{idx + 1}</TableCell>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-center">{item.received_quantity} {item.unit}</TableCell>
                        <TableCell className="text-right font-mono">₹{item.unit_price}</TableCell>
                        <TableCell className="text-right font-mono font-medium">₹{(item.received_quantity * item.unit_price).toLocaleString('en-IN')}</TableCell>
                      </TableRow>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setViewDialog(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
